import { useMutation, useQuery } from '@apollo/client'
import { DeviceInfo } from '@capacitor/device/dist/esm/definitions'
import { useCallback, useEffect, useState } from 'react'

import { gql } from '@/clients/utoniq-core'
import {
  ChargePrepaidPointProcessMutation,
  ChargePrepaidPointProcessMutationVariables,
  EscottMasterInput,
  Maybe,
  PaymentMethod,
  PurchasablePrepaidPointDetailFragment,
  PurchasablePrepaidPointsQuery,
  PurchasablePrepaidPointsQueryVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useAnalytics, useTrackViewItem } from '@/core/analytics'
import {
  ConversionCategories,
  ConversionListName,
} from '@/core/analytics/ConversionKeys'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { useTranslation } from '@/core/i18n'
import { App } from '@/core/native'
import { useDeviceInfo } from '@/core/native/device'
import { purchase } from '@/core/payment'
import { generateTransactionId } from '@/lib/analytics'
import {
  isCarrierPaymentMethod,
  submitEscottForm,
  useBFCacheRestore,
} from '@/lib/escott'
import { createUuid } from '@/lib/uuid'

import { Action, fragments, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { loading: deviceInfoLoading, deviceInfo } = useDeviceInfo()
  const { user } = useCurrentUser()
  const {
    purchaseByIosAndAndroid,
    purchaseByWeb,
    completeNativePurchase,
    setCompleteNativePurchase,
    chargeLoading,
  } = usePointPurchase(user?.uid, deviceInfo?.platform, 'prepaidPointCharge')

  const { data, loading, fetchMore } = useQuery<
    PurchasablePrepaidPointsQuery,
    PurchasablePrepaidPointsQueryVariables
  >(PURCHASABLE_PREPAID_POINTS, {
    variables: { platform: deviceInfo?.platform || '' },
    skip: deviceInfoLoading,
    pollInterval: completeNativePurchase ? 2000 : 0,
  })

  // 決済後、prepaidPointBalanceが更新されたらpollingを停止する
  useEffect(() => {
    setCompleteNativePurchase(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.prepaidPointBalance])

  // GA: view_itemの計測
  useTrackViewItem<PurchasablePrepaidPointsQuery>(data, data => {
    return data?.purchasablePrepaidPoints.edges.map(edge => {
      const { node: plan } = edge

      return {
        item_category: ConversionCategories.prepaidPoint,
        item_category2: plan.prepaidPointId,
        item_category3: plan.metadata.platform,
        item_list_name: ConversionListName.purchasablePrepaidPointList,
        item_id: plan.metadata.id,
        item_name: plan.metadata.id,
        price: plan.price,
        quantity: 1,
      }
    })
  })

  return {
    state: {
      loading,
      hasNextPage: !!data?.purchasablePrepaidPoints?.pageInfo?.hasNextPage,
      chargeLoading,
      point: data?.prepaidPointBalance?.balance ?? null,
      purchasablePrepaidPoints: data?.purchasablePrepaidPoints ?? null,
      paymentSource: data?.paymentSource ?? null,
      /** アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
      enablePointCarrierPayment:
        !!data?.organization?.modules?.pointCarrierPayment,
    },
    actions: {
      onClickCharge:
        deviceInfo?.platform === 'web'
          ? purchaseByWeb
          : purchaseByIosAndAndroid,

      async onFetchMore() {
        await fetchMore({
          variables: {
            platform: deviceInfo?.platform || '',
            pageInput: {
              cursor:
                data?.purchasablePrepaidPoints?.pageInfo?.endCursor ?? null,
            },
          },
        })
      },
    },
  }
})

const PURCHASABLE_PREPAID_POINTS = gql`
  query purchasablePrepaidPoints($platform: String!, $pageInput: PageInput) {
    prepaidPointBalance {
      prepaidPointId
      balance
    }
    purchasablePrepaidPoints(platform: $platform, pageInput: $pageInput) {
      ...PurchasablePrepaidPointConnection
    }
    paymentSource {
      ...PrepaidPointsPaymentSource
    }
    organization {
      id
      modules {
        pointCarrierPayment
      }
    }
  }
  ${fragments.purchasablePrepaidPoints}
`

const CHARGE_PREPAID_POINT_PROCESS = gql`
  mutation ChargePrepaidPointProcess(
    $inputs: ChargePrepaidPointInput!
    $paymentMethod: PaymentMethod
    $escottInput: EscottMasterInput
  ) {
    chargePrepaidPointProcess(
      inputs: $inputs
      paymentMethod: $paymentMethod
      escottInput: $escottInput
    ) {
      __typename
      ... on PrepaidPointTransaction {
        ...PrepaidPointChargeResult
      }
      ... on EscottMasterResult {
        nextActionUrl
        parameter {
          encryptValue
          merchantId
          openID
          operateId
        }
      }
    }
  }
  ${fragments.prepaidPointTransaction}
`

const CHARGE_PREPAID_POINTS_BY_IAP = gql`
  mutation ChargePrepaidPointByIapMutation(
    $inputs: ChargePrepaidPointIapInput!
  ) {
    chargePrepaidPointByIap(inputs: $inputs) {
      ...PrepaidPointChargeResult
    }
  }
  ${fragments.prepaidPointTransaction}
`

export const usePointPurchase = (
  userId?: string,
  platform?: DeviceInfo['platform'],
  /** ページ名測定用 */
  pageName?: string
) => {
  const { analytics } = useAnalytics()
  // 外部キャリア決済画面（escott）へのリダイレクト中かどうかを管理するフラグ
  // mutation の完了から escott フォーム送信までに
  // submit ボタンが活性化してしまうのを防ぐために用いている
  const [isRedirectingToEscott, setIsRedirectingToEscott] = useState(false)
  // ネイティブで決済モーダルが出る前のボタンのローディング
  const [purchaseCalling, setPurchaseCalling] = useState(false)
  // ネイティブ決済完了後、増加分のポイント取得クエリをポーリングするフラグ
  const [completeNativePurchase, setCompleteNativePurchase] = useState(false)
  const { t } = useTranslation('prepaidPointCharge')

  // ポイント残高を取得するクエリ
  const { refetch: refetchPoints } = useQuery<
    PurchasablePrepaidPointsQuery,
    PurchasablePrepaidPointsQueryVariables
  >(PURCHASABLE_PREPAID_POINTS, {
    variables: { platform: platform || '' },
    skip: true, // 初回読み込み時は実行しない
  })

  const [chargePrepaidPointProcess, { loading: prepaidPointCharging }] =
    useMutation<
      ChargePrepaidPointProcessMutation,
      ChargePrepaidPointProcessMutationVariables
    >(CHARGE_PREPAID_POINT_PROCESS, {
      onCompleted({ chargePrepaidPointProcess }) {
        switch (chargePrepaidPointProcess.__typename) {
          // 決済がすぐに完了する場合
          case 'PrepaidPointTransaction':
            break
          // 外部キャリア決済画面にて追加手続きが必要なケース
          // キャリア決済を想定している
          case 'EscottMasterResult':
            // 現在保有しているポイントを再取得する
            refetchPoints({
              platform: platform || '',
            })

            // 外部のキャリア決済画面へフォーム送信によってリダイレクト
            // 後続処理が外部画面とバックエンドで実行された後に
            // ステータスに応じて escottInput で指定した画面に復帰する
            submitEscottForm(
              chargePrepaidPointProcess.nextActionUrl,
              chargePrepaidPointProcess.parameter
            )
            break
          default:
            break
        }
      },
    })

  const purchaseByWeb = useCallback(
    async (
      plan: PurchasablePrepaidPointDetailFragment,
      paymentMethod: Maybe<PaymentMethod>,
      escottInput: EscottMasterInput
    ) => {
      // キャリア決済の場合はローディング状態を表す
      // state.subscribing に渡している
      // isRedirectingToEscott を true にする
      // mutation の完了から外部キャリア決済画面に遷移するまでの間に
      // submit ボタンが活性化するのを防ぐために行う
      if (isCarrierPaymentMethod(paymentMethod)) {
        setIsRedirectingToEscott(true)
      }
      await chargePrepaidPointProcess({
        variables: {
          inputs: {
            prepaidPointId: plan.prepaidPointId,
            purchasablePrepaidPointId: plan.id,
          },
          paymentMethod,
          escottInput,
        },
        context: {
          headers: { 'x-utoniq-idempotency-key': createUuid() },
        },
      })
      // GA:Conevrsion計測イベント
      analytics?.track('click_purchase', {
        item_id: plan.metadata.id,
        item_name: plan.prepaidPointId,
        platform: plan.metadata.platform,
        pageName,
      })
      analytics?.ecommerce({
        transaction_id: generateTransactionId(userId),
        currency: 'JPY',
        type: 'credit_card',
        items: [
          {
            item_category: ConversionCategories.prepaidPoint,
            item_category2: plan.prepaidPointId,
            item_category3: plan.metadata.platform,
            item_list_name: ConversionListName.purchasablePrepaidPointList,
            item_id: plan.metadata.id,
            item_name: plan.metadata.id,
            price: plan.price,
            quantity: 1,
          },
        ],
      })

      return
    },
    [analytics, chargePrepaidPointProcess, userId]
  )

  const purchaseByIosAndAndroid = useCallback(
    async (plan: PurchasablePrepaidPointDetailFragment) => {
      setPurchaseCalling(true)

      await purchase(
        plan.metadata.id,
        App.getPlatformResult({
          ios: config.revenueCatApiKey.ios,
          android: config.revenueCatApiKey.android,
          web: '',
        }),
        plan.price,
        userId,
        undefined,
        t
      ).finally(() => {
        setPurchaseCalling(false)
      })

      // ネイティブ決済完了後、webhookでの反映が遅い場合の対策として10秒毎にpolling
      setCompleteNativePurchase(true)

      // GA:Conevrsion計測イベント
      analytics?.track('click_purchase', {
        id: plan.id,
        name: plan.prepaidPointId,
        pageName,
        platform: platform,
      })
      analytics?.ecommerce({
        transaction_id: generateTransactionId(userId),
        currency: 'JPY',
        type: 'credit_card',
        items: [
          {
            item_category: ConversionCategories.prepaidPoint,
            item_category2: plan.prepaidPointId,
            item_category3: plan.metadata.platform,
            item_list_name: 'purchasable_prepaid_point_list',
            item_id: plan.metadata.id,
            item_name: plan.metadata.id,
            price: plan.price,
            quantity: 1,
          },
        ],
      })
      return
    },
    [analytics, platform, userId]
  )

  // 外部キャリア決済画面（escott）からブラウザバックした際
  // リダイレクト中かどうかを管理するフラグをリセットする
  // ブラウザバック時に BFCache が効き再読み込みが走らない環境で
  // ローディング状態が解除されないことを防ぐ
  useBFCacheRestore(() => {
    setIsRedirectingToEscott(false)
  })

  return {
    purchaseByWeb,
    purchaseByIosAndAndroid,
    completeNativePurchase,
    setCompleteNativePurchase,
    chargeLoading:
      purchaseCalling || prepaidPointCharging || isRedirectingToEscott,
  }
}
